import { createClient } from '@supabase/supabase-js'
import { Database } from '../types/database'

// Função para obter variáveis de ambiente com fallback
const getEnvVar = (key: string, fallback?: string): string => {
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key] || fallback || ''
  }
  // Fallback para casos onde import.meta.env não está disponível
  return fallback || ''
}

const supabaseUrl = getEnvVar('VITE_SUPABASE_URL')
const supabaseAnonKey = getEnvVar('VITE_SUPABASE_ANON_KEY')

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Add timeout for auth operations
    flowType: 'pkce'
  },
  global: {
    // Adicionar headers para evitar problemas de CORS
    headers: {
      'X-Client-Info': 'atividade-pronta-platform'
    },
    // Add fetch configuration with timeout
    fetch: (url, options = {}) => {
      const controller = new AbortController()
      // Increased timeout from 15s to 45s to support synchronous generation of up to 10 questions
      // Rationale: 5 questions take ~41s, so 10 questions need ~82s, but Edge Functions have 60s limit
      // 45s provides a safe buffer for small batches while preventing indefinite hangs
      const timeoutId = setTimeout(() => controller.abort(), 45000) // 45 second timeout

      return fetch(url, {
        ...options,
        signal: controller.signal
      }).finally(() => {
        clearTimeout(timeoutId)
      })
    }
  },
  db: {
    // Desabilitar cache para evitar problemas com RLS
    schema: 'public'
  },
  realtime: {
    // Add timeout for realtime connections
    timeout: 10000, // 10 seconds
    heartbeatIntervalMs: 30000, // 30 seconds
    reconnectAfterMs: (tries: number) => Math.min(tries * 1000, 10000) // Max 10 seconds
  }
})

// Utility function to add timeout to any Supabase query
// PromiseLike (não Promise): os builders do PostgREST são thenables, e o tipo
// Promise<T> aqui quebrava a inferência de T (data/error viravam unknown)
export const withTimeout = <T>(
  promise: PromiseLike<T>,
  timeoutMs: number = 10000,
  errorMessage: string = 'Query timeout'
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(errorMessage)), timeoutMs)
    })
  ])
}

// Adicionar função RPC para buscar perfil sem problemas de RLS e timeout
export const getProfileById = async (userId: string) => {
  try {
    const profileQuery = supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    const { data, error } = await withTimeout(
      profileQuery,
      8000, // 8 second timeout for profile queries
      'Profile fetch timeout'
    )

    if (error) throw error
    return data
  } catch (error) {
    return null
  }
}

// Connection health check function
export const checkSupabaseConnection = async (): Promise<boolean> => {
  try {
    const { error } = await withTimeout(
      supabase.from('profiles').select('id').limit(1),
      5000,
      'Connection check timeout'
    )
    return !error
  } catch (error) {
    return false
  }
}